import React, { useState, useEffect, useRef } from 'react';
import { saveAs } from 'file-saver';
import {
  Columns3,
  Download,
  Upload,
  Plus,
  Trash2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { defaultSettings, defaultColumnMappings } from '../utils/defaultSettings.js';

const requiredHeaders = ['esasNo', 'sanikSayisi', 'magdurSayisi', 'klasorSayisi'];

/** Excel’de görünebilecek sütun başlıkları için anahtar → kullanıcı etiketi */
const FIELD_LABELS = {
  esasNo: 'Esas numarası',
  sanikSayisi: 'Sanık sayısı',
  magdurSayisi: 'Mağdur sayısı',
  klasorSayisi: 'Klasör sayısı',
  gorus: 'Görüş / talep',
  eskiHakim: 'Eski hakim',
  sucTuru: 'Suç türü',
  manuelTevziEdilecekHakim: 'Manuel tevzi edilecek hakim',
  manuelTevziHakim: 'Manuel atanan hakim',
};

export const ColumnSettingsPage = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const importInputRef = useRef(null);

  useEffect(() => {
    const savedSettings = localStorage.getItem('columnSettings');
    if (!savedSettings) return;
    try {
      const parsed = JSON.parse(savedSettings);
      setSettings({
        ...defaultSettings,
        ...parsed,
        columnMappings: {
          ...defaultColumnMappings,
          ...(parsed.columnMappings || {}),
        },
      });
    } catch {
      /* ignore */
    }
  }, []);

  const persistSettings = (next) => {
    setSettings(next);
    localStorage.setItem('columnSettings', JSON.stringify(next));
  };

  const handleColumnMappingChange = (key, index, value) => {
    const updatedMappings = { ...settings.columnMappings };
    if (!updatedMappings[key]) {
      updatedMappings[key] = [];
    }
    updatedMappings[key] = [...updatedMappings[key]];
    updatedMappings[key][index] = value;
    persistSettings({ ...settings, columnMappings: updatedMappings });
  };

  const addColumnMapping = (key) => {
    const updatedMappings = { ...settings.columnMappings };
    if (!updatedMappings[key]) {
      updatedMappings[key] = [];
    }
    updatedMappings[key] = [...updatedMappings[key], ''];
    persistSettings({ ...settings, columnMappings: updatedMappings });
  };

  const removeColumnMapping = (key, index) => {
    const updatedMappings = { ...settings.columnMappings };
    updatedMappings[key] = updatedMappings[key].filter((_, i) => i !== index);
    persistSettings({ ...settings, columnMappings: updatedMappings });
  };

  const exportSettings = () => {
    const blob = new Blob([JSON.stringify(settings, null, 2)], {
      type: 'application/json',
    });
    saveAs(blob, 'column_settings.json');
  };

  const importSettings = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        if (!imported || typeof imported !== 'object') {
          alert('Dosya geçerli bir nesne içermiyor.');
          return;
        }
        if (
          imported.columnMappings != null &&
          typeof imported.columnMappings !== 'object'
        ) {
          alert('columnMappings alanı geçersiz.');
          return;
        }
        const merged = {
          ...defaultSettings,
          ...imported,
          columnMappings: {
            ...defaultColumnMappings,
            ...(imported.columnMappings || {}),
          },
        };
        persistSettings(merged);
      } catch {
        alert('Geçersiz JSON dosyası. Lütfen doğru formatta bir dosya seçin.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const requiredCount = requiredHeaders.length;
  const optionalCount = Object.keys(settings.columnMappings || {}).filter(
    (k) => !requiredHeaders.includes(k)
  ).length;

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-slate-50 via-white to-slate-50/90 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 border-b border-slate-200/80 pb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-500/25">
                  <Columns3 className="h-5 w-5" aria-hidden />
                </span>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  Sütun ayarları
                </h1>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                Excel dosyanızdaki sütun başlıkları, uygulamanın beklediği alan adlarıyla birebir
                eşleşmeyebilir. Her alan için Excel’de kullanabileceğiniz olası başlık metinlerini
                ekleyin; içe aktarma sırasında ilk eşleşen başlık kullanılır.
              </p>
            </div>
          </div>
        </header>

        <div
          className="mb-8 flex flex-col gap-3 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm ring-1 ring-amber-900/5 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <div className="flex items-start gap-2 sm:items-center">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
            <p>
              <span className="font-semibold text-amber-900">{requiredCount} zorunlu alan</span>
              {optionalCount > 0 ? (
                <>
                  {' '}
                  ve <span className="font-medium">{optionalCount} isteğe bağlı alan</span> için
                  eşleştirme tanımlayabilirsiniz. Zorunlu alanlar kırmızı kenarlıkla işaretlenir.
                </>
              ) : (
                <> için eşleştirme tanımlanmalıdır. Zorunlu alanlar kırmızı kenarlıkla işaretlenir.</>
              )}
            </p>
          </div>
        </div>

        <section className="mb-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportSettings}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Download className="h-4 w-4" aria-hidden />
              Dışa aktar
            </button>
            <button
              type="button"
              onClick={() => importInputRef.current?.click()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Upload className="h-4 w-4" aria-hidden />
              İçe aktar
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              className="sr-only"
              onChange={importSettings}
            />
          </div>
          <p className="text-xs text-slate-500">
            Ayarlar tarayıcıda saklanır; <code className="rounded bg-slate-100 px-1 py-0.5 text-slate-800">column_settings.json</code>{' '}
            ile yedekleyebilirsiniz.
          </p>
        </section>

        <section aria-labelledby="mapping-heading">
          <div className="mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" aria-hidden />
            <h2 id="mapping-heading" className="text-lg font-semibold text-slate-900">
              Sütun eşleştirmeleri
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Object.entries(settings.columnMappings || {}).map(([key, alternatives]) => {
              const isRequired = requiredHeaders.includes(key);
              const label = FIELD_LABELS[key] || key;
              return (
                <article
                  key={key}
                  className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md ${
                    isRequired
                      ? 'border-red-300/90 ring-red-500/10'
                      : 'border-slate-200/90'
                  }`}
                >
                  <div
                    className={`border-b px-4 py-3 ${
                      isRequired ? 'border-red-100 bg-red-50/50' : 'border-slate-100 bg-slate-50/90'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-slate-900">{label}</h3>
                        <code className="mt-1 block truncate text-xs text-slate-500" title={key}>
                          {key}
                        </code>
                      </div>
                      {isRequired && (
                        <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-800 ring-1 ring-red-200">
                          Zorunlu
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <p className="text-xs leading-relaxed text-slate-500">
                      Excel başlığında geçebilecek metinler (soldan ilk eşleşen kullanılır).
                    </p>
                    <ul className="space-y-2">
                      {(alternatives.length ? alternatives : ['']).map((alt, index) => (
                        <li key={`${key}-${index}`}>
                          <div className="flex items-stretch gap-2">
                            <label className="sr-only" htmlFor={`col-${key}-${index}`}>
                              {label} — eşleşme {index + 1}
                            </label>
                            <input
                              id={`col-${key}-${index}`}
                              type="text"
                              value={alt}
                              onChange={(e) =>
                                handleColumnMappingChange(key, index, e.target.value)
                              }
                              placeholder="Örn: Esas No"
                              className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner shadow-slate-900/5 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              onClick={() => removeColumnMapping(key, index)}
                              disabled={alternatives.length <= 1}
                              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-slate-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                              title="Bu satırı sil"
                              aria-label={`${label} eşleşmesini sil`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => addColumnMapping(key)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/50 px-3 py-2 text-sm font-medium text-indigo-800 transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      Başlık eşleşmesi ekle
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ColumnSettingsPage;
